<template>
  <div class="assign-type-fields">
    <!-- 顶部分隔线 -->
    <el-divider
      v-if="texts.dividerTitle"
      content-position="left"
    >
      <span class="assign-type-fields__divider-text">{{ texts.dividerTitle }}</span>
    </el-divider>
    <!-- 分配方式 -->
    <el-form-item
      :label="texts.assignTypeLabel"
      :label-width="texts.labelWidth"
    >
      <el-radio-group
        v-model="model.assignType"
        @change="onAssignTypeChange"
      >
        <el-radio v-if="allowNone" label="none">
          {{ texts.noneRadioLabel }}
        </el-radio>
        <el-radio label="user">
          {{ texts.userRadioLabel }}
        </el-radio>
        <el-radio label="team">
          {{ texts.teamRadioLabel }}
        </el-radio>
      </el-radio-group>
    </el-form-item>

    <!-- 执行人员 -->
    <el-form-item
      v-if="model.assignType === 'user'"
      :label="texts.userFieldLabel"
      :label-width="texts.labelWidth"
      :prop="userFormProp"
    >
      <el-select
        :key="'assign-user-' + model.assignType"
        v-model="model.assignUserIds"
        multiple
        collapse-tags
        filterable
        :placeholder="texts.userPlaceholder"
        style="width: 100%"
        :loading="userLoading"
        :disabled="showNoUserHint"
      >
        <el-option
          v-for="u in userCandidates"
          :key="u.capacityCode"
          :label="u.capacityName"
          :value="u.capacityCode"
        >
          <span>{{ u.capacityName }}</span>
          <span v-if="texts.showUserCodeInOption">({{ u.userCode }})</span>
          <span class="assign-type-fields__user-tel">{{ u.tel }}</span>
        </el-option>
      </el-select>
      <!-- 暂无执行人员提示 -->
      <div
        v-if="showNoUserHint"
        class="assign-empty-guide"
      >
        <i class="el-icon-user assign-empty-guide__icon" />
        <div class="assign-empty-guide__text">
          <div class="assign-empty-guide__title">
            {{ texts.userEmptyTitle }}
          </div>
          <div class="assign-empty-guide__desc">
            {{ texts.userEmptyDesc }}
          </div>
          <div class="assign-empty-guide__link-row">
            <el-button
              type="text"
              class="assign-empty-guide__link"
              @click="$emit('navigate-to-org-user')"
            >
              {{ texts.userEmptyLinkText }}<i class="el-icon-arrow-right" />
            </el-button>
          </div>
        </div>
      </div>
    </el-form-item>

    <!-- 执行小组 -->
    <el-form-item
      v-if="model.assignType === 'team'"
      :label="texts.teamFieldLabel"
      :label-width="texts.labelWidth"
      :prop="teamFormProp"
    >
      <el-select
        v-model="model.assignTeamId"
        filterable
        :placeholder="texts.teamPlaceholder"
        style="width: 100%"
        :loading="teamLoading"
        :disabled="showNoTeamHint"
      >
        <el-option
          v-for="t in teamOptions"
          :key="t.uuid"
          :label="t.teamName"
          :value="t.uuid"
        />
      </el-select>
      <!-- 暂无执行小组提示 -->
      <div
        v-if="showNoTeamHint"
        class="assign-empty-guide"
      >
        <i class="el-icon-s-custom assign-empty-guide__icon" />
        <div class="assign-empty-guide__text">
          <div class="assign-empty-guide__title">
            {{ texts.teamEmptyTitle }}
          </div>
          <div class="assign-empty-guide__desc">
            {{ texts.teamEmptyDesc }}
          </div>
          <div class="assign-empty-guide__link-row">
            <el-button
              type="text"
              class="assign-empty-guide__link"
              @click="$emit('navigate-to-team')"
            >
              {{ texts.teamEmptyLinkText }}<i class="el-icon-arrow-right" />
            </el-button>
          </div>
        </div>
      </div>
    </el-form-item>
  </div>
</template>

<script>
import { teamService } from '@/features/gnmk/team/team.service';

/**
 * 任务/故障等场景共用的「分配方式 + 指定个人/小组」表单项。
 * 内部按 teamType 请求小组与候选人，父级只需传入包含 assignType、assignUserIds、assignTeamId 的 model。
 */
const PRESETS = {
  task: {
    /** 顶部分隔线默认不展示，由使用 task 预设的父级通过 prop dividerTitle 传入 */
    dividerTitle: '',
    assignTypeLabel: '执行人员分配',
    userFieldLabel: '执行人员',
    teamFieldLabel: '执行小组',
    noneRadioLabel: '不指定（所有任务执行员可见）',
    userRadioLabel: '指定个人',
    teamRadioLabel: '指定小组',
    userPlaceholder: '请选择执行人员（可多选）',
    teamPlaceholder: '请选择小组（只提供选择巡查测试类型小组）',
    userEmptyTitle: '暂无可选执行人员',
    userEmptyDesc: '请先在组织人员管理中维护具有任务执行员岗位的人员，保存后再回到本页选择。',
    userEmptyLinkText: '去组织人员管理',
    teamEmptyTitle: '暂无巡查测试类型的人员小组',
    teamEmptyDesc: '请先在人员小组中创建对应类型的小组，保存后再回到本页选择。',
    teamEmptyLinkText: '去创建人员小组',
    showUserCodeInOption: true,
    labelWidth: '120px'
  },
  fault: {
    dividerTitle: '',
    assignTypeLabel: '维修人员分配',
    userFieldLabel: '指定人员',
    teamFieldLabel: '指定小组',
    noneRadioLabel: '不指定（所有故障处理员可见）',
    userRadioLabel: '指定个人',
    teamRadioLabel: '指定小组',
    userPlaceholder: '选择维修人员（可多选）',
    teamPlaceholder: '选择维修小组',
    userEmptyTitle: '暂无可选维修人员',
    userEmptyDesc: '请先在组织人员管理中维护具备故障处理员岗位的人员，保存后再回到本页选择。',
    userEmptyLinkText: '去组织人员管理',
    teamEmptyTitle: '暂无故障维修类型的人员小组',
    teamEmptyDesc: '请先在人员小组中创建对应类型的小组，保存后再回到本页选择。',
    teamEmptyLinkText: '去创建人员小组',
    showUserCodeInOption: false,
    labelWidth: '100px'
  }
};

export default {
  name: 'AssignTypeFields',
  props: {
    allowNone: { type: Boolean, default: true },
    /** 须包含 assignType、assignUserIds、assignTeamId，与父表单同一引用 */
    model: {
      type: Object,
      required: true
    },
    /** 与后端 teamType 一致：task 巡查测试类，fault 故障维修类 */
    teamType: {
      type: String,
      required: true,
      validator: v => ['task', 'fault'].includes(v)
    },
    /** 文案预设：仅 task / fault 两套基础文案 */
    preset: {
      type: String,
      default: 'task',
      validator: v => ['task', 'fault'].includes(v)
    },
    /**
     * 顶部分隔线标题：不传则用 preset 默认；传空字符串则不显示分隔线。
     * 任务组第一步需展示时可在父级传入，例如「默认执行人员分配（可选）」。
     */
    dividerTitle: {
      type: String,
      default: undefined
    },
    /** 表单项 label 宽度，不传则随 preset */
    labelWidth: {
      type: String,
      default: ''
    },
    /** 覆盖 preset 中的占位与标签（改派弹窗等场景） */
    userPlaceholder: {
      type: String,
      default: undefined
    },
    teamPlaceholder: {
      type: String,
      default: undefined
    },
    assignTypeLabel: {
      type: String,
      default: undefined
    },
    userFieldLabel: {
      type: String,
      default: undefined
    },
    teamFieldLabel: {
      type: String,
      default: undefined
    },
    noneRadioLabel: {
      type: String,
      default: undefined
    },
    /**
     * 为 true 时拉取/刷新小组列表并重置人员懒加载状态（用于弹窗每次打开）
     */
    active: {
      type: Boolean,
      default: true
    },
    /** 与 el-form 校验 prop 对齐，任务场景可不传 */
    userFormProp: {
      type: String,
      default: null
    },
    teamFormProp: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      userCandidates: [],
      teamOptions: [],
      userLoading: false,
      teamLoading: false
    };
  },
  computed: {
    texts () {
      const base = PRESETS[this.preset] || PRESETS.task;
      const ov = (v, b) => (v !== undefined ? v : b);
      return {
        ...base,
        dividerTitle: ov(this.dividerTitle, base.dividerTitle),
        labelWidth: this.labelWidth || base.labelWidth,
        userPlaceholder: ov(this.userPlaceholder, base.userPlaceholder),
        teamPlaceholder: ov(this.teamPlaceholder, base.teamPlaceholder),
        assignTypeLabel: ov(this.assignTypeLabel, base.assignTypeLabel),
        userFieldLabel: ov(this.userFieldLabel, base.userFieldLabel),
        teamFieldLabel: ov(this.teamFieldLabel, base.teamFieldLabel),
        noneRadioLabel: ov(this.noneRadioLabel, base.noneRadioLabel)
      };
    },
    showNoTeamHint () {
      if (this.model.assignType !== 'team') return false;
      return !this.teamLoading && (!this.teamOptions || this.teamOptions.length === 0);
    },
    showNoUserHint () {
      if (this.model.assignType !== 'user') return false;
      return !this.userLoading && (!this.userCandidates || this.userCandidates.length === 0);
    }
  },
  methods: {
    onAssignTypeChange (val) {
      if (val !== 'user') {
        this.model.assignUserIds = [];
      }
      if (val !== 'team') {
        this.model.assignTeamId = null;
      }
      if (val === 'user') {
        this.userCandidates = [];
        this.loadUserCandidates();
      }
      if (val === 'team') {
        this.teamOptions = [];
        this.loadTeamOptions();
      }
      this.$emit('assign-type-change', val);
    },
    loadUserCandidates () {
      this.userLoading = true;
      teamService.getUserCandidatesList({ teamType: this.teamType })
        .then(res => { this.userCandidates = res.data || []; })
        .finally(() => { this.userLoading = false; });
    },
    loadTeamOptions () {
      this.teamLoading = true;
      teamService.queryTeamList({ teamType: this.teamType })
        .then(res => { this.teamOptions = res.data || []; })
        .finally(() => { this.teamLoading = false; });
    }
  },
  watch: {
    active: {
      immediate: true,
      handler (v) {
        if (v) {
          this.loadTeamOptions();
          this.userCandidates = [];
          // 改派弹窗打开时若已是「指定个人」，需拉候选人（无 assignType 变更事件）
          this.$nextTick(() => {
            if (this.model.assignType === 'user') {
              this.loadUserCandidates();
            }
          });
        }
      }
    }
  }
};
</script>

<style scoped lang="scss">
.assign-type-fields__divider-text {
  font-size: 13px;
  color: #909399;
}
.assign-type-fields__user-tel {
  float: right;
  font-size: 12px;
  color: #909399;
}
.assign-empty-guide {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  margin-top: 12px;
  border-radius: 8px;
  background: linear-gradient(145deg, #f5f9ff 0%, #eef6ff 100%);
  border: 1px solid #c6e2ff;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.08);
}
.assign-empty-guide__icon {
  flex-shrink: 0;
  margin-top: 2px;
  font-size: 22px;
  color: #409eff;
}
.assign-empty-guide__text {
  flex: 1;
  min-width: 0;
}
.assign-empty-guide__title {
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  letter-spacing: 0.02em;
}
.assign-empty-guide__desc {
  font-size: 13px;
  line-height: 1.65;
  color: #606266;
}
.assign-empty-guide__link-row {
  margin-top: 8px;
}
.assign-empty-guide__link {
  margin-left: 0;
  font-weight: 500;
}
::v-deep .assign-empty-guide__link.el-button--text {
  padding-left: 0;
}
.assign-empty-guide__link i {
  margin-left: 2px;
  font-size: 12px;
}
</style>
